import React from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import FavoriteIcon from '@mui/icons-material/Favorite';

const Footer = () => {
  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%)`,
        backdropFilter: 'blur(10px)',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        py: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CurrencyExchangeIcon 
                sx={{ 
                  fontSize: 28, 
                  mr: 1,
                  color: theme.palette.secondary.light,
                }} 
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.5px',
                }}
              >
                RatePly
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.8),
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              Your trusted currency converter and exchange rate tool. 
              Get real-time rates for 170+ currencies worldwide.
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                mb: 2,
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Currency Converter', href: '/converter' },
                { label: 'Exchange Rates', href: '/rates' },
                { label: 'Popular Pairs', href: '/rates' },
              ].map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: theme.palette.secondary.light,
                      textDecoration: 'underline',
                    },
                    transition: 'color 0.2s ease-in-out',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Legal */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'white',
                mb: 2,
              }}
            >
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Service', href: '/terms' },
                { label: 'Cookie Policy', href: '/cookies' },
              ].map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  sx={{
                    color: alpha(theme.palette.common.white, 0.8),
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: theme.palette.secondary.light,
                      textDecoration: 'underline',
                    },
                    transition: 'color 0.2s ease-in-out',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Box
          sx={{
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pt: 3,
            mt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                fontSize: '0.875rem',
              }}
            >
              © {new Date().getFullYear()} RatePly. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy', href: '/privacy' },
                { label: 'Terms', href: '/terms' },
                { label: 'Cookies', href: '/cookies' },
              ].map((link, index) => (
                <React.Fragment key={link.label}>
                  <Link
                    href={link.href}
                    sx={{
                      color: alpha(theme.palette.common.white, 0.7),
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      '&:hover': {
                        color: theme.palette.secondary.light,
                        textDecoration: 'underline',
                      },
                      transition: 'color 0.2s ease-in-out',
                    }}
                  >
                    {link.label}
                  </Link>
                  {index < 2 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.5),
                        fontSize: '0.875rem',
                      }}
                    >
                      •
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                fontSize: '0.875rem',
              }}
            >
              Made with
            </Typography>
            <FavoriteIcon 
              sx={{ 
                fontSize: 16, 
                color: theme.palette.error.light,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' },
                },
              }} 
            />
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                fontSize: '0.875rem',
              }}
            >
              for global commerce
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
